import React, { useEffect, useState } from 'react';
import { createListing, updateListing } from '../../api';
import { assetUrl } from '../../utils';
import { amenityFields, createBlankListing, propertyFields, rentFields } from '../../admin/listingConfig';

const ListingForm = ({
  initialListing = null,
  initialType = 'rent',
  onSaved,
  onCancel,
  compact = false,
}) => {
  const [form, setForm] = useState(createBlankListing(initialType));
  const [mainImageFile, setMainImageFile] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (initialListing) {
      setForm({
        ...createBlankListing(initialListing.listingType || initialType),
        ...initialListing,
        rentDetails: {
          ...createBlankListing('rent').rentDetails,
          ...(initialListing.rentDetails || {}),
        },
        propertyDetails: {
          ...createBlankListing('property').propertyDetails,
          ...(initialListing.propertyDetails || {}),
          amenities: {
            ...createBlankListing('property').propertyDetails.amenities,
            ...((initialListing.propertyDetails && initialListing.propertyDetails.amenities) || {}),
          },
        },
        galleryImages: initialListing.galleryImages || [],
      });
    } else {
      setForm(createBlankListing(initialType));
    }
    setMainImageFile(null);
    setGalleryFiles([]);
    setError('');
    setMessage('');
  }, [initialListing, initialType]);

  const updateNested = (section, key, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: value,
      },
    }));
  };

  const updateAmenity = (key, value) => {
    setForm((prev) => ({
      ...prev,
      propertyDetails: {
        ...prev.propertyDetails,
        amenities: {
          ...prev.propertyDetails.amenities,
          [key]: value,
        },
      },
    }));
  };

  const removeMainImage = () => {
    setForm((prev) => ({ ...prev, mainImage: '' }));
    setMainImageFile(null);
  };

  const removeGalleryImage = (image) => {
    setForm((prev) => ({
      ...prev,
      galleryImages: prev.galleryImages.filter((item) => item !== image),
    }));
  };

  const removeGalleryFile = (file) => {
    setGalleryFiles((prev) => prev.filter((item) => item !== file));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');

    try {
      const payload = {
        ...form,
        galleryImages: form.galleryImages,
      };

      const fd = new FormData();
      fd.append('payload', JSON.stringify(payload));
      if (mainImageFile) fd.append('mainImage', mainImageFile);
      galleryFiles.forEach((file) => fd.append('galleryImages', file));

      const listing = initialListing ? await updateListing(initialListing._id, fd) : await createListing(fd);
      setMessage(initialListing ? 'Listing updated.' : 'Listing created.');
      if (onSaved) onSaved(listing);

      if (!initialListing) {
        setForm(createBlankListing(initialType));
        setMainImageFile(null);
        setGalleryFiles([]);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Unable to save listing');
    } finally {
      setSaving(false);
    }
  };

  const wrapperClass = compact
    ? 'rounded-[18px] border border-[#262626] bg-[#111] p-[1rem]'
    : 'w-full rounded-[18px] border border-[#262626] bg-[#111] p-[1.25rem]';

  return (
    <form onSubmit={handleSubmit} className={wrapperClass}>
      <div className="flex items-center justify-between gap-[1rem]">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-[#EEC24B]">
            {form.listingType === 'rent' ? 'Rent / Lease' : 'Property Listing'}
          </p>
          <h2 className="mt-[0.2rem] text-[24px] font-bold text-white">{initialListing ? 'Edit listing' : 'Create listing'}</h2>
        </div>
        {onCancel ? (
          <button type="button" onClick={onCancel} className="rounded-lg border border-[#262626] px-[0.9rem] py-[0.7rem] text-[14px] font-semibold">
            Close
          </button>
        ) : null}
      </div>

      <div className="mt-[1rem] grid gap-[1rem] md:grid-cols-2">
        <label className="flex flex-col gap-[0.4rem]">
          <span className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[#999]">Listing type</span>
          <select
            value={form.listingType}
            onChange={(e) => setForm((prev) => ({ ...prev, listingType: e.target.value }))}
            className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.85rem] text-white outline-none"
          >
            <option value="rent">For Rent / Lease</option>
            <option value="property">Property Listing</option>
          </select>
        </label>
        <label className="flex flex-col gap-[0.4rem]">
          <span className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[#999]">Status</span>
          <select
            value={form.status}
            onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
            className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.85rem] text-white outline-none"
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
          </select>
        </label>
        <label className="flex flex-col gap-[0.4rem] md:col-span-2">
          <span className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[#999]">Title</span>
          <input
            value={form.title}
            onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
            className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.85rem] text-white outline-none"
          />
        </label>
        <label className="flex items-center gap-[0.6rem] md:col-span-2">
          <input type="checkbox" checked={form.featured} onChange={(e) => setForm((prev) => ({ ...prev, featured: e.target.checked }))} />
          <span className="text-[15px] text-[#BDBDBD]">Featured listing</span>
        </label>
      </div>

      <div className="mt-[1rem] grid gap-[1rem] md:grid-cols-2">
        <label className="flex flex-col gap-[0.4rem] md:col-span-2">
          <span className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[#999]">Summary</span>
          <textarea
            value={form.summary}
            onChange={(e) => setForm((prev) => ({ ...prev, summary: e.target.value }))}
            rows="3"
            className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.85rem] text-white outline-none"
          />
        </label>
        <label className="flex flex-col gap-[0.4rem] md:col-span-2">
          <span className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[#999]">Description</span>
          <textarea
            value={form.description}
            onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
            rows="5"
            className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.85rem] text-white outline-none"
          />
        </label>
      </div>

      <div className="mt-[1rem] grid gap-[1rem] md:grid-cols-3">
        <label className="flex flex-col gap-[0.4rem]">
          <span className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[#999]">External URL</span>
          <input
            value={form.externalUrl}
            onChange={(e) => setForm((prev) => ({ ...prev, externalUrl: e.target.value }))}
            className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.85rem] text-white outline-none"
          />
        </label>
        <label className="flex flex-col gap-[0.4rem]">
          <span className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[#999]">Sample Website</span>
          <input
            value={form.sampleWebsite}
            onChange={(e) => setForm((prev) => ({ ...prev, sampleWebsite: e.target.value }))}
            className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.85rem] text-white outline-none"
          />
        </label>
        <label className="flex flex-col gap-[0.4rem]">
          <span className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[#999]">Map URL</span>
          <input
            value={form.mapUrl}
            onChange={(e) => setForm((prev) => ({ ...prev, mapUrl: e.target.value }))}
            className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.85rem] text-white outline-none"
          />
        </label>
      </div>

      <div className="mt-[1rem] grid gap-[1rem] md:grid-cols-2">
        <label className="flex flex-col gap-[0.4rem]">
          <span className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[#999]">Main image</span>
          <input
            key={mainImageFile ? mainImageFile.name : 'main-image-empty'}
            type="file"
            accept="image/*"
            onChange={(e) => setMainImageFile(e.target.files?.[0] || null)}
            className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.8rem] text-white outline-none"
          />
        </label>
        <label className="flex flex-col gap-[0.4rem]">
          <span className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[#999]">Gallery images</span>
          <input
            key={galleryFiles.map((file) => `${file.name}-${file.lastModified}`).join('|') || 'gallery-images-empty'}
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => setGalleryFiles(Array.from(e.target.files || []))}
            className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.8rem] text-white outline-none"
          />
        </label>
      </div>

      {(form.mainImage || mainImageFile || form.galleryImages.length || galleryFiles.length) ? (
        <div className="mt-[1rem] grid gap-[0.85rem] md:grid-cols-[200px_1fr]">
          {form.mainImage || mainImageFile ? (
            <div className="relative overflow-hidden rounded-lg border border-[#262626] bg-[#1A1A1A]">
              <img
                src={mainImageFile ? URL.createObjectURL(mainImageFile) : assetUrl(form.mainImage)}
                alt="Main property preview"
                className="h-[170px] w-full object-cover"
              />
              <button
                type="button"
                onClick={removeMainImage}
                aria-label="Remove main image"
                className="absolute right-[0.5rem] top-[0.5rem] rounded-full bg-black/75 px-[0.55rem] py-[0.25rem] text-[18px] leading-none text-white transition-colors hover:bg-red-700"
              >
                &times;
              </button>
            </div>
          ) : (
            <div className="flex h-[170px] items-center justify-center rounded-lg border border-dashed border-[#262626] bg-[#1A1A1A] text-center text-[13px] text-[#999]">
              No main image
            </div>
          )}
          <div className="rounded-lg border border-[#262626] bg-[#1A1A1A] p-[1rem]">
            <p className="text-[13px] uppercase tracking-[0.12em] text-[#999]">Gallery preview</p>
            {form.galleryImages.length || galleryFiles.length ? (
              <div className="mt-[0.75rem] flex flex-wrap gap-[0.75rem]">
                {form.galleryImages.map((src, index) => (
                  <div key={`saved-${src}-${index}`} className="relative">
                    <img src={assetUrl(src)} alt={`Gallery preview ${index + 1}`} className="h-[76px] w-[76px] rounded-lg object-cover" />
                    <button
                      type="button"
                      onClick={() => removeGalleryImage(src)}
                      aria-label={`Remove gallery image ${index + 1}`}
                      className="absolute right-[-0.35rem] top-[-0.35rem] rounded-full bg-black/80 px-[0.35rem] py-[0.1rem] text-[15px] leading-none text-white hover:bg-red-700"
                    >
                      &times;
                    </button>
                  </div>
                ))}
                {galleryFiles.map((file, index) => (
                  <div key={`new-${file.name}-${file.lastModified}-${index}`} className="relative">
                    <img src={URL.createObjectURL(file)} alt={`New gallery preview ${index + 1}`} className="h-[76px] w-[76px] rounded-lg object-cover" />
                    <button
                      type="button"
                      onClick={() => removeGalleryFile(file)}
                      aria-label={`Remove selected gallery image ${index + 1}`}
                      className="absolute right-[-0.35rem] top-[-0.35rem] rounded-full bg-black/80 px-[0.35rem] py-[0.1rem] text-[15px] leading-none text-white hover:bg-red-700"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            ) : <p className="mt-[0.75rem] text-[14px] text-[#999]">No gallery images</p>}
          </div>
        </div>
      ) : null}

      {form.listingType === 'rent' ? (
        <div className="mt-[1.25rem] rounded-[18px] border border-[#262626] bg-[#1A1A1A] p-[1rem]">
          <h3 className="text-[18px] font-bold text-white">Rent fields</h3>
          <div className="mt-[1rem] grid gap-[1rem] md:grid-cols-2">
            {rentFields.map(([key, label]) => (
              <label key={key} className="flex flex-col gap-[0.4rem]">
                <span className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[#999]">{label}</span>
                <input
                  value={form.rentDetails[key]}
                  onChange={(e) => updateNested('rentDetails', key, e.target.value)}
                  className="rounded-lg border border-[#262626] bg-[#111] px-[0.95rem] py-[0.85rem] text-white outline-none"
                />
              </label>
            ))}
            {[
              ['aboutProperty', 'About Property'],
              ['propertyOverview', 'Property Overview'],
              ['locationOverview', 'Location Overview'],
            ].map(([key, label]) => (
              <label key={key} className="flex flex-col gap-[0.4rem] md:col-span-2">
                <span className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[#999]">{label}</span>
                <textarea
                  rows="4"
                  value={form.rentDetails[key]}
                  onChange={(e) => updateNested('rentDetails', key, e.target.value)}
                  className="rounded-lg border border-[#262626] bg-[#111] px-[0.95rem] py-[0.85rem] text-white outline-none"
                />
              </label>
            ))}
          </div>
        </div>
      ) : (
        <div className="mt-[1.25rem] rounded-[18px] border border-[#262626] bg-[#1A1A1A] p-[1rem]">
          <h3 className="text-[18px] font-bold text-white">Property listing fields</h3>
          <div className="mt-[1rem] grid gap-[1rem] md:grid-cols-2">
            {propertyFields.map(([key, label]) => (
              <label key={key} className="flex flex-col gap-[0.4rem]">
                <span className="text-[13px] font-semibold uppercase tracking-[0.12em] text-[#999]">{label}</span>
                <input
                  value={form.propertyDetails[key]}
                  onChange={(e) => updateNested('propertyDetails', key, e.target.value)}
                  className="rounded-lg border border-[#262626] bg-[#111] px-[0.95rem] py-[0.85rem] text-white outline-none"
                />
              </label>
            ))}
          </div>
          <div className="mt-[1rem] rounded-[16px] border border-[#262626] bg-[#111] p-[1rem]">
            <h4 className="text-[16px] font-semibold text-[#EEC24B]">Amenities</h4>
            <div className="mt-[1rem] grid gap-[1rem] md:grid-cols-2">
              {amenityFields.map((field) => {
                const value = form.propertyDetails.amenities[field.key];

                return (
                  <label
                    key={field.key}
                    className={`flex flex-col gap-[0.4rem] ${field.kind === 'textarea' ? 'md:col-span-2' : ''}`}
                  >
                    <span className="text-[12px] font-semibold uppercase tracking-[0.11em] text-[#999]">{field.label}</span>
                    {field.kind === 'select' ? (
                      <select
                        value={value}
                        onChange={(e) => updateAmenity(field.key, e.target.value)}
                        className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.8rem] text-white outline-none"
                      >
                        <option value="">Select</option>
                        {field.options.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    ) : field.kind === 'number' ? (
                      <input
                        type="number"
                        min={field.min}
                        step={field.step}
                        value={value}
                        onChange={(e) => updateAmenity(field.key, e.target.value)}
                        className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.8rem] text-white outline-none"
                      />
                    ) : field.kind === 'textarea' ? (
                      <textarea
                        rows={field.rows || 3}
                        value={value}
                        onChange={(e) => updateAmenity(field.key, e.target.value)}
                        className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.8rem] text-white outline-none"
                      />
                    ) : (
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => updateAmenity(field.key, e.target.value)}
                        className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.8rem] text-white outline-none"
                      />
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {error && <p className="mt-[1rem] rounded-lg border border-red-900/40 bg-red-950/30 px-[1rem] py-[0.85rem] text-red-200">{error}</p>}
      {message && <p className="mt-[1rem] rounded-lg border border-emerald-900/40 bg-emerald-950/30 px-[1rem] py-[0.85rem] text-emerald-200">{message}</p>}

      <div className="mt-[1.25rem] flex flex-wrap gap-[0.75rem]">
        <button
          type="submit"
          disabled={saving}
          className="rounded-lg bg-[#EEC24B] px-[1.1rem] py-[0.9rem] text-[15px] font-semibold text-black disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saving ? 'Saving...' : initialListing ? 'Update listing' : 'Create listing'}
        </button>
        {onCancel ? (
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-[#262626] px-[1.1rem] py-[0.9rem] text-[15px] font-semibold text-white"
          >
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
};

export default ListingForm;
